import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  TradeData,
  TradeType,
  UploadAction,
  ValidationError,
  ValidateResponse,
  UploadResult,
  UploadResponse,
  UploadAfterValidateResponse,
} from '../../../../Types/TradeUpload';
import { uploadAfterValidateAPI, uploadFileToAPI } from '../../../../api/tradeUploadService';
import Accordion from 'react-bootstrap/esm/Accordion';

// Color constants
const COLORS = {
  errorBg: '#FEE2E2',
  successBg: '#DCFCE7',
  errorText: '#B91C1C',
  successText: '#1E7F5C',
  validationBg: '#FEF2F2',
  successRowBg: '#F0FDF4',
  errorRowBg: '#ffebee',
  successRowBgLight: '#e8f5e9',
  errorRowBgLight: '#ffcdd2',
};

interface TradeDataTableProps {
  data: TradeData[];
  failedRowSet: Set<number>;
  rowErrorColumnsMap: Map<number, Set<string>>;
  maxHeight?: number;
}

const TradeDataTable: React.FC<TradeDataTableProps> = ({
  data,
  failedRowSet,
  rowErrorColumnsMap,
  maxHeight = 360,
}) => (
  <TableContainer
    sx={{
      maxHeight,
      mb: 2,
      border: '1px solid #E5E7EB',
      borderRadius: 1.5,
    }}
  >
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          <TableCell
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              backgroundColor: '#F9FAFB',
              position: 'sticky',
              left: 0,
              zIndex: 2,
              minWidth: 40,
            }}
          >
            #
          </TableCell>
          {Object.keys(data?.[0] || {}).map((col) => (
            <TableCell
              key={col}
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                backgroundColor: '#F9FAFB',
              }}
            >
              {col.replace(/_/g, ' ')}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {(data || []).map((row: any, idx: number) => {
          const isFailed = failedRowSet.has(idx);
          const errorCols = rowErrorColumnsMap.get(idx) || new Set<string>();

          return (
            <TableRow
              key={idx}
              hover
              sx={{
                backgroundColor: isFailed ? COLORS.validationBg : COLORS.successRowBg,
                borderBottom: '1px solid #E5E7EB',
              }}
            >
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  backgroundColor: isFailed ? COLORS.errorBg : COLORS.successBg,
                }}
              >
                {idx + 1}
              </TableCell>
              {Object.entries(row).map(([col, value], colIdx) => {
                const isColError = errorCols.has(col);
                return (
                  <TableCell
                    key={colIdx}
                    sx={{
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap',
                      backgroundColor: isColError ? COLORS.errorBg : 'inherit',
                      color: isColError ? COLORS.errorText : '#111827',
                      fontWeight: isColError ? 600 : 400,
                    }}
                  >
                    {typeof value === 'object'
                      ? JSON.stringify(value)
                      : String(value)}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

interface StatItem {
  label: string;
  value: number | string;
  color: string;
}

interface StatsGridProps {
  items: StatItem[];
}

const StatsGrid: React.FC<StatsGridProps> = ({ items }) => (
  <Grid
    container
    alignItems="center"
    sx={{
      mb: 2,
      px: 1.5,
      py: 1,
      border: '1px solid #E5E7EB',
      borderRadius: 1.5,
      backgroundColor: '#F9FAFB',
    }}
  >
    {items.map((item, idx) => (
      <Grid
        key={idx}
        item
        xs={12 / items.length}
        sx={
          idx > 0 && idx < items.length
            ? { borderLeft: '1px solid #E5E7EB', px: 1.5, textAlign: 'center' }
            : idx === items.length - 1
              ? { borderLeft: '1px solid #E5E7EB', textAlign: 'right' }
              : {}
        }
      >
        <Typography
          sx={{
            fontSize: '0.6rem',
            fontWeight: 600,
            color: '#6B7280',
            letterSpacing: '0.04em',
          }}
        >
          {item.label}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.95rem',
            fontWeight: 700,
            color: item.color,
            lineHeight: 1.2,
          }}
        >
          {item.value}
        </Typography>
      </Grid>
    ))}
  </Grid>
);

interface ExecuteButtonProps {
  loading: boolean;
  onClick: () => void;
}

const ExecuteButton: React.FC<ExecuteButtonProps> = ({ loading, onClick }) => (
  <Button
    variant="contained"
    onClick={onClick}
    disabled={loading}
    size="small"
    sx={{
      py: 0.6,
      px: 2,
      fontSize: '0.7rem',
      fontWeight: 600,
      bgcolor: '#00bcd4',
      '&:hover': { bgcolor: '#00acc1' },
    }}
  >
    {loading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CircularProgress size={16} sx={{ color: '#FFFFFF' }} />
        <span>Executing...</span>
      </Box>
    ) : (
      'Execute'
    )}
  </Button>
);

interface LocationState {
  file: File;
  tradeType: TradeType;
  previewData: TradeData[];
}

type Steps = 'preview' | 'validated' | 'uploaded';

const TradeUploadPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  const [validateOnly, setValidateOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[] | null>(null);
  const [successMessage, setSuccessMessage] = useState<UploadResult | null>(null);
  const [currentStep, setCurrentStep] = useState<Steps>('preview');
  const [validationResult, setValidationResult] = useState<ValidateResponse | null>(null);
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | UploadAfterValidateResponse | null>(null);

  useEffect(() => {
    if (!state || !state.file || !state.previewData) {
      navigate('/');
    }
  }, [state, navigate]);

  const { file, tradeType, previewData } = state || { file: null, tradeType: TradeType.CLEARED, previewData: [] };

  const { displayedData, rowErrorColumnsMap, failedRowSet } = useMemo(() => {
    const map = new Map<number, Set<string>>();
    const failed = new Set<number>();
    const data = (validationResult && (validationResult as any).data) || previewData || [];

    if (Array.isArray(data)) {
      data.forEach((row: any, idx: number) => {
        const errColsRaw = row?.error_rows;
        if (errColsRaw) {
          failed.add(idx);
          const cols = String(errColsRaw)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          map.set(idx, new Set(cols));
        }
      });
    }

    return { displayedData: data, rowErrorColumnsMap: map, failedRowSet: failed };
  }, [validationResult, previewData]);

  const { uploadDisplayedData, uploadRowErrorColumnsMap, uploadFailedRowSet } = useMemo(() => {
    const map = new Map<number, Set<string>>();
    const failed = new Set<number>();
    const data = (uploadResponse && (uploadResponse as any).data) || [];

    if (Array.isArray(data)) {
      data.forEach((row: any, idx: number) => {
        const errColsRaw = row?.error_rows;
        if (errColsRaw) {
          failed.add(idx);
          const cols = String(errColsRaw)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          map.set(idx, new Set(cols));
        }
      });
    }

    return { uploadDisplayedData: data, uploadRowErrorColumnsMap: map, uploadFailedRowSet: failed };
  }, [uploadResponse]);

  if (!state || !state.file || !state.previewData) {
    return null;
  }

  const handleExecute = async (): Promise<void> => {
    if (!file) {
      setValidationErrors([{ row: 0, column: 'general', message: 'No file selected' }]);
      return;
    }

    setLoading(true);
    setValidationErrors(null);
    setSuccessMessage(null);

    try {
      if (currentStep === 'preview' && validateOnly) {
        const result: ValidateResponse = await uploadFileToAPI(file, tradeType, UploadAction.VALIDATE);
        setValidationResult(result);

        if (result.error_rows > 0 && result.errors && result.errors.length > 0) {
          setValidationErrors(result.errors);
        }

        setCurrentStep('validated');

      } else if (currentStep === 'preview' && !validateOnly) {

        const uploadResult: UploadResponse = await uploadFileToAPI(file, tradeType, UploadAction.UPLOAD);
        setUploadResponse(uploadResult);

        const successMsg: UploadResult = {
          type: 'upload',
          message: `Upload completed: ${uploadResult.created_count || 0} trades created, ${uploadResult.failed_count || 0} failed`,
          executionIds: uploadResult.created || [],
          rowCount: uploadResult.created_count || 0,
          createdCount: uploadResult.created_count || 0,
          failedCount: uploadResult.failed_count || 0,
          duplicateCount: uploadResult.duplicates?.length || 0,
          tradeType: tradeType,
          timestamp: new Date().toLocaleString(),
        };

        setSuccessMessage(successMsg);
        setCurrentStep('uploaded');

      } else if (currentStep === 'validated') {

        const validatedData: TradeData[] = (validationResult?.data as TradeData[]) ?? [];
        const uploadResult: UploadAfterValidateResponse = await uploadAfterValidateAPI(validatedData);
        setUploadResponse(uploadResult);

        const successMsg: UploadResult = {
          type: 'upload',
          message: `Upload completed: ${uploadResult.created_count || 0} trades created, ${uploadResult.failed_count || 0} failed`,
          executionIds: uploadResult.created || [],
          rowCount: uploadResult.created_count || 0,
          createdCount: uploadResult.created_count || 0,
          failedCount: uploadResult.failed_count || 0,
          duplicateCount: uploadResult.duplicates?.length || 0,
          tradeType: tradeType,
          timestamp: new Date().toLocaleString(),
        };

        setSuccessMessage(successMsg);
        setCurrentStep('uploaded');
      }

    } catch (error: unknown) {
      let errorMessage = 'Unknown error occurred';
      let errorDetails: ValidationError[] = [];

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if ((error as any)?.response?.data?.errors) {
        errorDetails = (error as any).response.data.errors as ValidationError[];
        errorMessage = `Validation failed with ${errorDetails.length} error(s)`;
      } else if ((error as any)?.response?.data?.message) {
        errorMessage = (error as any).response.data.message as string;
      }

      setValidationErrors(
        errorDetails.length > 0
          ? errorDetails
          : [{ row: 0, column: 'general', message: errorMessage }]
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = (): void => {
    navigate(-1);
  };

  const allRowsValid = validationResult && validationResult.error_rows === 0;

  return (
    <div className="page-body">
      <div className="table-ad">
        <Accordion defaultActiveKey="0" className="table-ad__addInputs">
          <Accordion.Item eventKey="0">
            <Accordion.Header className="accordion_button">
              Trade Upload
            </Accordion.Header>
            <Accordion.Body>
              <Container maxWidth={false} sx={{ py: 2, width: '100%' }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Button
                    startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={handleBack}
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                  >
                    Back
                  </Button>
                  <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.25rem' }}>
                    Trade Upload
                  </Typography>
                </Box>

                <Card
                  sx={{
                    mb: 2,
                    borderRadius: 1.5,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  }}
                >
                  <CardContent
                    sx={{
                      px: 2,
                      py: 1.25,
                      '&:last-child': { pb: 1.25 },
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      {/* Trade Type */}
                      <Grid item xs={4}>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            color: '#6B7280',
                            letterSpacing: '0.06em',
                          }}
                        >
                          TYPE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#111827',
                            textTransform: 'capitalize',
                            lineHeight: 1.2,
                          }}
                        >
                          {tradeType}
                        </Typography>
                      </Grid>

                      {/* Rows */}
                      <Grid item xs={4}>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            color: '#6B7280',
                            letterSpacing: '0.06em',
                          }}
                        >
                          ROWS
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#111827',
                            lineHeight: 1.2,
                          }}
                        >
                          {previewData.length}
                        </Typography>
                      </Grid>

                      {/* File Name */}
                      <Grid item xs={4}>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            color: '#6B7280',
                            letterSpacing: '0.06em',
                          }}
                        >
                          FILE NAME
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#1F2937',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.3,
                          }}
                          title={file.name}
                        >
                          {file.name}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                {/* Preview Data Table - Show in preview step */}
                {currentStep === 'preview' && (
                  <Card sx={{ mb: 2, boxShadow: 2 }}>
                    <CardHeader
                      title="Preview Data"
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 600,
                        fontSize: '1rem'
                      }}
                      sx={{ py: 1.5, px: 2 }}
                    />
                    <CardContent sx={{ py: 0, px: 2, pb: 2 }}>
                      <TableContainer sx={{ maxHeight: '500px', mb: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: '#fafafa',
                                  zIndex: 10,
                                  py: 0.75,
                                  px: 1.5,
                                  borderRight: '1px solid #e0e0e0',
                                  minWidth: 40,
                                }}
                              >
                                #
                              </TableCell>
                              {previewData.length > 0 &&
                                Object.keys(previewData[0]).map((col) => (
                                  <TableCell
                                    key={col}
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      py: 0.75,
                                      px: 1.5,
                                      whiteSpace: 'nowrap',
                                      backgroundColor: '#fafafa'
                                    }}
                                  >
                                    {col}
                                  </TableCell>
                                ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {previewData.map((row, idx) => (
                              <TableRow
                                key={idx}
                                hover
                                sx={{
                                  '&:hover': { backgroundColor: '#f5f5f5' },
                                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                                }}
                              >
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 5,
                                    py: 0.5,
                                    px: 1.5,
                                    backgroundColor: 'inherit',
                                    borderRight: '1px solid #e0e0e0',
                                  }}
                                >
                                  {idx + 1}
                                </TableCell>
                                {Object.values(row).map((value, colIdx) => (
                                  <TableCell
                                    key={colIdx}
                                    sx={{
                                      fontSize: '0.7rem',
                                      py: 0.5,
                                      px: 1.5,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {typeof value === 'object'
                                      ? JSON.stringify(value)
                                      : String(value)}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={validateOnly}
                              onChange={(e) => setValidateOnly(e.target.checked)}
                              disabled={loading}
                              size="small"
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                              Validate Only (no data insertion)
                            </Typography>
                          }
                          sx={{ display: 'flex', alignItems: 'center' }}
                        />
                      </Box>

                      <ExecuteButton loading={loading} onClick={handleExecute} />
                    </CardContent>
                  </Card>
                )}

                {/* Validation Result Table - Show after validation */}
                {currentStep === 'validated' && validationResult && (
                  <Card
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      backgroundColor: '#FFFFFF',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <CardHeader
                      avatar={
                        allRowsValid ? (
                          <CheckCircleIcon sx={{ color: '#1E7F5C', fontSize: 20 }} />
                        ) : (
                          <WarningIcon sx={{ color: '#9A6B16', fontSize: 20 }} />
                        )
                      }
                      title="Validation Summary"
                      subheader={
                        allRowsValid
                          ? 'All records passed validation checks'
                          : 'Some records require attention before execution'
                      }
                      titleTypographyProps={{
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#111827',
                      }}
                      subheaderTypographyProps={{
                        fontSize: '0.75rem',
                        color: '#6B7280',
                      }}
                      sx={{ px: 2.5, py: 2 }}
                    />

                    <CardContent sx={{ pt: 0, px: 2.5 }}>
                      <StatsGrid
                        items={[
                          { label: 'Valid Trades', value: validationResult.success_rows, color: COLORS.successText },
                          { label: 'Error Trades', value: validationResult.error_rows, color: COLORS.errorText },
                          { label: 'TRADE TYPE', value: tradeType, color: '#111827' },
                        ]}
                      />

                      <TradeDataTable
                        data={displayedData}
                        failedRowSet={failedRowSet}
                        rowErrorColumnsMap={rowErrorColumnsMap}
                      />

                      {validationErrors && validationErrors.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            fontSize="0.75rem"
                            fontWeight={600}
                            color="#991B1B"
                            sx={{ mb: 0.5 }}
                          >
                            Validation Issues (showing first 5)
                          </Typography>

                          {validationErrors.slice(0, 5).map((err, idx) => (
                            <Typography
                              key={idx}
                              fontSize="0.7rem"
                              color="#6B7280"
                              sx={{ lineHeight: 1.6 }}
                            >
                              Row {err.row}, Column "{err.column}": {err.message}
                            </Typography>
                          ))}

                          {validationErrors.length > 5 && (
                            <Typography
                              fontSize="0.7rem"
                              color="#6B7280"
                              sx={{ fontStyle: 'italic', mt: 0.5 }}
                            >
                              …and {validationErrors.length - 5} more issues
                            </Typography>
                          )}
                        </Box>
                      )}

                      {allRowsValid && (
                        <ExecuteButton loading={loading} onClick={handleExecute} />
                      )}
                    </CardContent>
                  </Card>
                )}

                {currentStep === 'uploaded' && uploadResponse && uploadResponse.error_rows > 0 && uploadDisplayedData.length > 0 && (
                  <Card
                    sx={{
                      mb: 2,
                      borderLeft: 3,
                      borderColor: 'error.main',
                      boxShadow: 2,
                    }}
                  >
                    <CardHeader
                      avatar={
                        <WarningIcon sx={{ color: 'error.main', fontSize: 22 }} />
                      }
                      title="Upload Errors"
                      titleTypographyProps={{
                        variant: 'subtitle2',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        color: 'error.main',
                      }}
                      sx={{ py: 1.5, px: 2 }}
                    />

                    <CardContent sx={{ pt: 0, pb: 2, px: 2 }}>
                      <Alert
                        severity="error"
                        sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}
                      >
                        Upload completed with errors: {uploadResponse.success_rows || 0} successful rows, {uploadResponse.error_rows} error rows
                      </Alert>

                      <StatsGrid
                        items={[
                          { label: 'SUCCESS ROWS', value: uploadResponse.success_rows || 0, color: COLORS.successText },
                          { label: 'ERROR ROWS', value: uploadResponse.error_rows, color: COLORS.errorText },
                          { label: 'TRADE TYPE', value: tradeType, color: '#111827' },
                        ]}
                      />

                      <TradeDataTable
                        data={uploadDisplayedData as TradeData[]}
                        failedRowSet={uploadFailedRowSet}
                        rowErrorColumnsMap={uploadRowErrorColumnsMap}
                        maxHeight={350}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Upload Success */}
                {currentStep === 'uploaded' && successMessage && !(uploadResponse && uploadResponse.error_rows > 0) && (
                  <Card sx={{ mb: 2, borderLeft: 3, borderColor: 'success.main', boxShadow: 2 }}>
                    <CardHeader
                      avatar={<CheckCircleIcon sx={{ color: 'success.main', fontSize: 26 }} />}
                      title="Upload Successful"
                      titleTypographyProps={{
                        variant: 'h6',
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: 'success.main',
                      }}
                      sx={{ py: 1.5, px: 2 }}
                    />
                    <CardContent sx={{ pt: 0, pb: 2, px: 2 }}>
                      <Alert severity="success" sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                        {successMessage.message}
                      </Alert>

                      <Paper sx={{ p: 1.5, backgroundColor: '#f1f8e9', mb: 1.5 }}>
                        <StatsGrid
                          items={[
                            { label: 'Created Trades', value: successMessage.createdCount || 0, color: COLORS.successText },
                            { label: 'Failed Trades', value: successMessage.failedCount || 0, color: COLORS.errorText },
                            { label: 'Duplicate Trades', value: successMessage.duplicateCount || 0, color: COLORS.errorText },
                            { label: 'TRADE TYPE', value: successMessage.tradeType, color: '#111827' },
                          ]}
                        />
                      </Paper>

                      {successMessage.executionIds && successMessage.executionIds.length > 0 && (
                        <Paper sx={{ p: 1.5, backgroundColor: '#e8f5e9', mb: 1.5 }}>
                          <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block', fontSize: '0.7rem' }}>
                            Created Execution IDs ({successMessage.executionIds.length})
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {successMessage.executionIds.slice(0, 10).map((id, idx) => (
                              <Chip
                                key={idx}
                                label={id}
                                color="success"
                                variant="outlined"
                                size="small"
                                sx={{ fontFamily: 'monospace', height: 20, fontSize: '0.65rem' }}
                              />
                            ))}
                            {successMessage.executionIds.length > 10 && (
                              <Chip
                                label={`+${successMessage.executionIds.length - 10} more`}
                                size="small"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </Paper>
                      )}

                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.7rem' }}>
                        Processed at: {successMessage.timestamp}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Container>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};

export default TradeUploadPage;